import { Dispatch, SetStateAction, useMemo } from 'react'

import { ViewContainer, useApi } from '@fortem-monorepo/react-spa'
import { ICGG2Report, ICGuidelines, STREAM_EVENTS } from '@fortem-monorepo/web-sdk'

import { StreamProgress } from '@/components/stream-progress/StreamProgress'
import { ReportControls } from '@/pages/funds/ic-guidelines-v2/components/ReportControls'
import { SECTIONS } from '@/pages/funds/ic-guidelines-v2/section-config'
import { useReportStream } from '@/react-query/use-report-stream'

export interface ReportTabProps {
  params: ICGuidelines.GetICGuidelinesParams
  setParams: Dispatch<SetStateAction<ICGuidelines.GetICGuidelinesParams>>
}

export const ReportTab: React.FC<ReportTabProps> = ({ params, setParams }) => {
  const api = useApi()

  const reportStream = useReportStream({
    reportType: 'ic_guidelines_v2',
    params,
    streamFn: api.icGuidelines.v2.streamReport,
  })

  const stageOutputs = useMemo(() => {
    return reportStream.state.events.reduce((outputs, event) => {
      if (event.event_type === STREAM_EVENTS.STAGE_COMPLETED && event.stage_id && event.output) {
        outputs[event.stage_id as keyof ICGG2Report.IcGuidelinesStageOutputs] = event.output
      }

      return outputs
    }, {} as ICGG2Report.IcGuidelinesStageOutputs)
  }, [reportStream.state.events])

  return (
    <>
      <ReportControls reportStream={reportStream} params={params} setParams={setParams} />
      <ViewContainer>
        {!reportStream.isIdle && (
          <StreamProgress
            title={`IC Guidelines for ${params?.fund_name} on ${new Date(params.value_date).toDateString()}`}
            stream={reportStream}
          />
        )}
        {(Object.keys(SECTIONS) as Array<keyof ICGG2Report.IcGuidelinesStageOutputs>).map(
          (stageId) => {
            const stage = renderStage(stageId, stageOutputs)

            return stage ? (
              <div key={stageId} className="mt-4">
                {stage}
              </div>
            ) : null
          },
        )}
      </ViewContainer>
    </>
  )
}

const renderStage = (
  stageId: keyof ICGG2Report.IcGuidelinesStageOutputs,
  stageOutputs: ICGG2Report.IcGuidelinesStageOutputs,
) => {
  switch (stageId) {
    case 'breaches': {
      const output = stageOutputs.breaches
      const Component = SECTIONS.breaches
      return output ? <Component output={output} stageOutputs={stageOutputs} /> : null
    }
    case 'summary': {
      const output = stageOutputs.summary
      const Component = SECTIONS.summary
      return output ? <Component output={output} /> : null
    }
    case 'income': {
      const output = stageOutputs.income
      const Component = SECTIONS.income
      return output ? <Component output={output} /> : null
    }
    case 'beta': {
      const output = stageOutputs.beta
      const Component = SECTIONS.beta
      return output ? <Component output={output} /> : null
    }
    case 'diversifiers': {
      const output = stageOutputs.diversifiers
      const Component = SECTIONS.diversifiers
      return output ? <Component output={output} /> : null
    }
    case 'protection': {
      const output = stageOutputs.protection
      const Component = SECTIONS.protection
      return output ? <Component output={output} /> : null
    }
  }
}
